"use client"

import { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import CloseIcon from "@mui/icons-material/Close"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PersonIcon from "@mui/icons-material/Person"
import InventoryIcon from "@mui/icons-material/Inventory"
import SellIcon from "@mui/icons-material/Sell"
import AnalyticsIcon from "@mui/icons-material/Analytics"
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Line,
  Area,
  AreaChart,
  ComposedChart,
} from "recharts"

const COLORS = [
  "#3B82F6", // Blue - Color primario para elementos principales
  "#10B981", // Emerald - Para métricas positivas y crecimiento
  "#F59E0B", // Amber - Para alertas y métricas de atención
  "#EF4444", // Red - Para métricas críticas o negativas
  "#8B5CF6", // Violet - Para diferenciación de categorías
  "#06B6D4", // Cyan - Para datos secundarios
  "#84CC16", // Lime - Para métricas de rendimiento
  "#F97316", // Orange - Para destacar elementos importantes
  "#EC4899", // Pink - Para categorías especiales
  "#6B7280", // Gray - Para elementos neutros
]

/**
 * FUNCIÓN UTILITARIA: formatCurrency
 *
 * Propósito:
 * - Formatea números como moneda argentina (ARS)
 * - Elimina decimales para mejor legibilidad en dashboards
 * - Utiliza separadores de miles localizados
 *
 * Configuración:
 * - Locale: es-AR (español argentino)
 * - Currency: ARS (peso argentino)
 * - MaximumFractionDigits: 0 (sin decimales)
 */
const formatCurrency = (value) => {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(value)
}

/**
 * COMPONENTE METRICCARD
 *
 * Propósito: Renderiza tarjetas de métricas con diseño profesional y efectos hover
 *
 * Funcionamiento:
 * 1. Recibe props: title (título), value (valor principal), subtitle (texto secundario),
 *    icon (ícono), color (tema de color), trend (tendencia opcional)
 * 2. Aplica gradiente de fondo basado en el color del tema
 * 3. Muestra ícono en avatar circular con colores temáticos
 * 4. Si hay trend, muestra indicador de tendencia con ícono y porcentaje
 * 5. Aplica efectos de hover (elevación y sombra)
 */
const MetricCard = ({ title, value, subtitle, icon: Icon, color = "primary", trend }) => {
  const theme = useTheme()

  return (
    <Card
      elevation={2}
      sx={{
        height: "100%",
        // Gradiente sutil basado en el color del tema
        background: `linear-gradient(135deg, ${theme.palette[color].main}08 0%, ${theme.palette[color].main}15 100%)`,
        border: `1px solid ${theme.palette[color].main}20`,
        transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette[color].main, mb: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: `${theme.palette[color].main}15`,
              color: theme.palette[color].main,
              width: 48,
              height: 48,
            }}
          >
            <Icon />
          </Avatar>
        </Box>
        {/* Indicador de tendencia opcional */}
        {trend && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            {trend > 0 ? (
              <TrendingUpIcon sx={{ color: "success.main", fontSize: 16, mr: 0.5 }} />
            ) : (
              <TrendingDownIcon sx={{ color: "error.main", fontSize: 16, mr: 0.5 }} />
            )}
            <Typography
              variant="caption"
              sx={{
                color: trend > 0 ? "success.main" : "error.main",
                fontWeight: 600,
              }}
            >
              {Math.abs(trend)}% vs período anterior
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * COMPONENTE TABPANEL
 *
 * Propósito: Maneja la visualización condicional del contenido de cada pestaña
 *
 * Funcionamiento:
 * 1. Recibe props: children (contenido), value (pestaña actual), index (índice de la pestaña)
 * 2. Solo renderiza el contenido cuando value === index
 * 3. Aplica atributos de accesibilidad para navegación por teclado
 */
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`chart-tabpanel-${index}`}
      aria-labelledby={`chart-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

/**
 * FUNCIÓN A11YPROPS
 *
 * Propósito: Genera propiedades de accesibilidad para las pestañas
 *
 * Funcionamiento:
 * 1. Recibe el índice de la pestaña
 * 2. Retorna objeto con id y aria-controls para navegación accesible
 */
function a11yProps(index) {
  return {
    id: `chart-tab-${index}`,
    "aria-controls": `chart-tabpanel-${index}`,
  }
}

/**
 * COMPONENTE PRINCIPAL: GRAFICASDETALLESVENTASMODAL
 *
 * Propósito: Modal de análisis detallado de ventas por items individuales
 *
 * Props:
 * - open: boolean - Controla la visibilidad del modal
 * - handleClose: function - Función para cerrar el modal
 * - data: array - Datos de detalles de ventas (items individuales de transacciones)
 * - loading: boolean - Estado de carga de datos
 *
 * Estructura de datos esperada:
 * - Cada item contiene: montoTotal, cantidad, item (descripción, código),
 *   Transaccion (id, fecha, entidad, usuario, transaccionPago)
 */
const GraficasDetallesVentasModal = ({ open, handleClose, data, loading = false }) => {
  const theme = useTheme()
  const [tabValue, setTabValue] = useState(0)

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  /**
   * CÁLCULO DE MÉTRICAS GENERALES
   *
   * Propósito: Procesa los datos para obtener KPIs principales del dashboard
   *
   * Funcionamiento:
   * 1. Suma todos los montos totales para obtener ventas totales
   * 2. Suma todas las cantidades para obtener items vendidos
   * 3. Cuenta transacciones únicas usando Set con IDs de transacción
   * 4. Cuenta clientes únicos usando Set con IDs de entidades
   * 5. Cuenta items únicos usando Set con IDs de items
   * 6. Calcula ticket promedio: ventas totales / transacciones únicas
   * 7. Calcula items por transacción: total items / transacciones únicas
   *
   * Retorna: Objeto con todas las métricas calculadas
   */
  const metricsGenerales = useMemo(() => {
    if (!data || data.length === 0) return {}

    const totalVentas = data.reduce((sum, item) => sum + (item.montoTotal || 0), 0)
    const totalItems = data.reduce((sum, item) => sum + (item.cantidad || 0), 0)
    const transaccionesUnicas = new Set(data.map((item) => item.Transaccion?.id)).size
    const clientesUnicos = new Set(data.map((item) => item.Transaccion?.entidad?.id)).size
    const itemsUnicos = new Set(data.map((item) => item.idItem)).size
    const ticketPromedio = transaccionesUnicas > 0 ? totalVentas / transaccionesUnicas : 0
    const itemsPorTransaccion = transaccionesUnicas > 0 ? totalItems / transaccionesUnicas : 0

    return {
      totalVentas,
      totalItems,
      transaccionesUnicas,
      clientesUnicos,
      itemsUnicos,
      ticketPromedio,
      itemsPorTransaccion,
    }
  }, [data])

  /**
   * FUNCIÓN: PROCESARTOP10ITEMS
   *
   * Propósito: Analiza y rankea los 10 items más vendidos por cantidad
   *
   * Funcionamiento:
   * 1. Crea objeto itemData para agrupar datos por item
   * 2. Itera sobre cada registro de venta:
   *    - Genera clave única: código + descripción del item
   *    - Acumula cantidad vendida y monto total
   *    - Registra transacciones únicas en Set
   * 3. Calcula métricas adicionales:
   *    - Precio promedio: monto total / cantidad vendida
   *    - Número de transacciones donde aparece el item
   * 4. Ordena por cantidad vendida (descendente)
   * 5. Retorna top 10 items con todas las métricas
   *
   * Retorna: Array de objetos con métricas por item
   */
  const procesarTop10Items = () => {
    const itemData = {}

    data.forEach((item) => {
      const itemNombre = item.item?.descripcion || "Sin descripción"
      const itemCodigo = item.item?.codigo || ""
      const itemKey = `${itemCodigo} - ${itemNombre}`

      if (!itemData[itemKey]) {
        itemData[itemKey] = {
          nombre: itemKey,
          cantidadVendida: 0,
          montoTotal: 0,
          precioPromedio: 0,
          transacciones: new Set(),
          margenBruto: 0,
        }
      }

      itemData[itemKey].cantidadVendida += item.cantidad || 0
      itemData[itemKey].montoTotal += item.montoTotal || 0
      itemData[itemKey].transacciones.add(item.Transaccion?.id)
    })

    // Calcular métricas adicionales para cada item
    Object.values(itemData).forEach((item) => {
      item.precioPromedio = item.cantidadVendida > 0 ? item.montoTotal / item.cantidadVendida : 0
      item.transaccionesCount = item.transacciones.size
      item.transacciones = undefined // Limpiar para evitar problemas de serialización
    })

    return Object.values(itemData)
      .sort((a, b) => b.cantidadVendida - a.cantidadVendida)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARTOP10CLIENTESPORFECHA
   *
   * Propósito: Analiza comportamiento y ranking de clientes por volumen de compras
   *
   * Funcionamiento:
   * 1. Crea objeto clienteData para agrupar por cliente
   * 2. Para cada registro de venta:
   *    - Identifica cliente por nombre + apellido de la entidad
   *    - Convierte fecha a formato local (es-AR)
   *    - Acumula ventas totales y cantidad de items
   *    - Registra fechas únicas y transacciones únicas en Sets
   *    - Actualiza fecha de última compra
   * 3. Calcula métricas avanzadas:
   *    - Días activos: número de fechas únicas de compra
   *    - Cantidad de compras: número de transacciones únicas
   *    - Ticket promedio: ventas totales / cantidad de compras
   *    - Items por compra: total items / cantidad de compras
   * 4. Ordena por total de ventas (descendente)
   * 5. Retorna top 10 clientes con análisis completo
   *
   * Retorna: Array de objetos con métricas detalladas por cliente
   */
  const procesarTop10ClientesPorFecha = () => {
    const clienteData = {}

    data.forEach((item) => {
      const clienteNombre =
        `${item.Transaccion?.entidad?.descripcion || "Sin nombre"} ${item.Transaccion?.entidad?.apellido || ""}`.trim()
      const fecha = new Date(item.Transaccion?.fecha).toLocaleDateString("es-AR")

      if (!clienteData[clienteNombre]) {
        clienteData[clienteNombre] = {
          nombre: clienteNombre,
          totalVentas: 0,
          cantidadCompras: 0,
          cantidadItems: 0,
          ultimaCompra: fecha,
          fechas: new Set(),
          transacciones: new Set(),
        }
      }

      clienteData[clienteNombre].totalVentas += item.montoTotal || 0
      clienteData[clienteNombre].cantidadItems += item.cantidad || 0
      clienteData[clienteNombre].fechas.add(fecha)
      clienteData[clienteNombre].transacciones.add(item.Transaccion?.id)

      // Actualizar última compra comparando fechas
      if (
        new Date(fecha.split("/").reverse().join("-")) >
        new Date(clienteData[clienteNombre].ultimaCompra.split("/").reverse().join("-"))
      ) {
        clienteData[clienteNombre].ultimaCompra = fecha
      }
    })

    return Object.values(clienteData)
      .map((cliente) => ({
        ...cliente,
        diasActivos: cliente.fechas.size,
        cantidadCompras: cliente.transacciones.size,
        ticketPromedio: cliente.cantidadCompras > 0 ? cliente.totalVentas / cliente.cantidadCompras : 0,
        itemsPorCompra: cliente.cantidadCompras > 0 ? cliente.cantidadItems / cliente.cantidadCompras : 0,
        fechas: undefined,
        transacciones: undefined,
      }))
      .sort((a, b) => b.totalVentas - a.totalVentas)
      .slice(0, 10)
  }

  /**
   * FUNCIÓN: PROCESARMEDIOSDEPAGO
   *
   * Propósito: Analiza distribución y uso de medios de pago en las ventas
   *
   * Funcionamiento:
   * 1. Crea objetos mediosData y tiposData para doble agrupación
   * 2. Para cada item de venta:
   *    - Accede a transaccionPago de la transacción
   *    - Para cada pago asociado:
   *      a) Identifica medio de pago específico y su tipo
   *      b) Calcula monto proporcional del item en ese pago:
   *         monto_pago * cotización * (monto_item / monto_total_transacción)
   *      c) Agrupa por medio específico (ej: "Visa", "Mastercard")
   *      d) Agrupa por tipo de medio (ej: "Tarjeta de Crédito", "Efectivo")
   *      e) Registra transacciones únicas para cada medio/tipo
   * 3. Procesa datos finales:
   *    - Convierte Sets de transacciones a contadores
   *    - Ordena medios por cantidad de transacciones
   *    - Ordena tipos por monto total
   * 4. Retorna objeto con ambos análisis: medios específicos y tipos
   *
   * Retorna: Objeto con arrays 'medios' y 'tipos', cada uno con métricas completas
   */
  const procesarMediosDePago = () => {
    const mediosData = {}
    const tiposData = {}

    data.forEach((item) => {
      const transaccionPagos = item.Transaccion?.transaccionPago || []

      transaccionPagos.forEach((tp) => {
        const pago = tp.pago
        if (pago?.medioDePago) {
          const medioNombre = pago.medioDePago.descripcion
          const tipoMedio = pago.medioDePago.tipoMedioDePago?.descripcion || "Sin tipo"
          // Cálculo proporcional del monto del item en este medio de pago
          const montoItem = pago.montoTotal * (pago.cotizacion || 1) * (item.montoTotal / item.Transaccion.montoTotal)

          // Agrupación por medio específico
          if (!mediosData[medioNombre]) {
            mediosData[medioNombre] = {
              nombre: medioNombre,
              tipo: tipoMedio,
              cantidadTransacciones: 0,
              montoTotal: 0,
              transacciones: new Set(),
            }
          }

          mediosData[medioNombre].transacciones.add(item.Transaccion?.id)
          mediosData[medioNombre].montoTotal += montoItem

          // Agrupación por tipo de medio
          if (!tiposData[tipoMedio]) {
            tiposData[tipoMedio] = {
              nombre: tipoMedio,
              cantidadTransacciones: 0,
              montoTotal: 0,
              transacciones: new Set(),
            }
          }

          tiposData[tipoMedio].transacciones.add(item.Transaccion?.id)
          tiposData[tipoMedio].montoTotal += montoItem
        }
      })
    })

    // Procesamiento final de datos
    const mediosFinales = Object.values(mediosData)
      .map((medio) => ({
        ...medio,
        cantidadTransacciones: medio.transacciones.size,
        transacciones: undefined,
      }))
      .sort((a, b) => b.cantidadTransacciones - a.cantidadTransacciones)

    const tiposFinales = Object.values(tiposData)
      .map((tipo) => ({
        ...tipo,
        cantidadTransacciones: tipo.transacciones.size,
        transacciones: undefined,
      }))
      .sort((a, b) => b.montoTotal - a.montoTotal)

    return { medios: mediosFinales, tipos: tiposFinales }
  }

  /**
   * FUNCIÓN: PROCESARTOPVENDEDORES
   *
   * Propósito: Analiza performance y métricas de productividad por vendedor
   *
   * Funcionamiento:
   * 1. Crea objeto vendedorData para agrupar por usuario vendedor
   * 2. Para cada item vendido:
   *    - Identifica vendedor por usuario de la transacción
   *    - Acumula ventas totales y cantidad de items
   *    - Registra en Sets: transacciones únicas, clientes únicos, items únicos
   * 3. Calcula métricas de performance:
   *    - Cantidad de transacciones realizadas
   *    - Número de clientes únicos atendidos
   *    - Variedad de items vendidos (diversificación)
   *    - Ticket promedio: ventas totales / transacciones
   *    - Items por transacción: productividad por venta
   * 4. Ordena por total de ventas (descendente)
   * 5. Retorna análisis completo de todos los vendedores
   *
   * Retorna: Array de objetos con métricas de performance por vendedor
   */
  const procesarTopVendedores = () => {
    const vendedorData = {}

    data.forEach((item) => {
      const vendedorNombre = item.Transaccion?.usuario?.usuario || "Sin vendedor"

      if (!vendedorData[vendedorNombre]) {
        vendedorData[vendedorNombre] = {
          nombre: vendedorNombre,
          totalVentas: 0,
          cantidadItems: 0,
          transacciones: new Set(),
          clientes: new Set(),
          itemsUnicos: new Set(),
        }
      }

      vendedorData[vendedorNombre].totalVentas += item.montoTotal || 0
      vendedorData[vendedorNombre].cantidadItems += item.cantidad || 0
      vendedorData[vendedorNombre].transacciones.add(item.Transaccion?.id)
      vendedorData[vendedorNombre].clientes.add(item.Transaccion?.entidad?.id)
      vendedorData[vendedorNombre].itemsUnicos.add(item.idItem)
    })

    return Object.values(vendedorData)
      .map((vendedor) => ({
        ...vendedor,
        cantidadTransacciones: vendedor.transacciones.size,
        clientesUnicos: vendedor.clientes.size,
        itemsUnicos: vendedor.itemsUnicos.size,
        ticketPromedio: vendedor.cantidadTransacciones > 0 ? vendedor.totalVentas / vendedor.cantidadTransacciones : 0,
        itemsPorTransaccion:
          vendedor.cantidadTransacciones > 0 ? vendedor.cantidadItems / vendedor.cantidadTransacciones : 0,
        transacciones: undefined,
        clientes: undefined,
      }))
      .sort((a, b) => b.totalVentas - a.totalVentas)
  }

  /**
   * FUNCIÓN: PROCESARANALISISTEMPOAL
   *
   * Propósito: Analiza evolución temporal de ventas día por día
   *
   * Funcionamiento:
   * 1. Crea objeto ventasPorDia para agrupar por fecha
   * 2. Para cada item vendido:
   *    - Convierte fecha de transacción a formato local (es-AR)
   *    - Acumula ventas totales y cantidad de items por día
   *    - Registra transacciones únicas por día en Set
   * 3. Calcula métricas diarias:
   *    - Total de ventas del día
   *    - Cantidad de items vendidos
   *    - Número de transacciones realizadas
   *    - Ticket promedio del día: ventas / transacciones
   * 4. Ordena cronológicamente por fecha
   * 5. Retorna serie temporal completa para gráficos de tendencias
   *
   * Retorna: Array ordenado por fecha con métricas diarias
   */
  const procesarAnalisisTemporal = () => {
    const ventasPorDia = {}

    data.forEach((item) => {
      const fecha = new Date(item.Transaccion?.fecha).toLocaleDateString("es-AR")

      if (!ventasPorDia[fecha]) {
        ventasPorDia[fecha] = {
          fecha,
          totalVentas: 0,
          cantidadItems: 0,
          transacciones: new Set(),
        }
      }

      ventasPorDia[fecha].totalVentas += item.montoTotal || 0
      ventasPorDia[fecha].cantidadItems += item.cantidad || 0
      ventasPorDia[fecha].transacciones.add(item.Transaccion?.id)
    })

    return Object.values(ventasPorDia)
      .map((dia) => ({
        ...dia,
        cantidadTransacciones: dia.transacciones.size,
        ticketPromedio: dia.cantidadTransacciones > 0 ? dia.totalVentas / dia.cantidadTransacciones : 0,
        transacciones: undefined,
      }))
      .sort(
        (a, b) => new Date(a.fecha.split("/").reverse().join("-")) - new Date(b.fecha.split("/").reverse().join("-")),
      )
  }

  const processChannelData = () => {
    const channelMap = new Map()

    data.forEach((detail) => {
      const canalEntidad = detail.Transaccion?.entidad?.canalEntidad
      if (canalEntidad) {
        const channelId = canalEntidad.id
        const channelName = canalEntidad.descripcion

        if (!channelMap.has(channelId)) {
          channelMap.set(channelId, {
            id: channelId,
            name: channelName,
            totalSales: 0,
            transactionCount: 0,
            totalQuantity: 0,
            uniqueCustomers: new Set(),
          })
        }

        const channel = channelMap.get(channelId)
        channel.totalSales += detail.montoTotal || 0
        channel.transactionCount += 1
        channel.totalQuantity += detail.cantidad || 0

        if (detail.Transaccion?.entidad?.id) {
          channel.uniqueCustomers.add(detail.Transaccion.entidad.id)
        }
      }
    })

    return Array.from(channelMap.values()).map((channel) => ({
      ...channel,
      uniqueCustomers: channel.uniqueCustomers.size,
      averageTicket: channel.transactionCount > 0 ? channel.totalSales / channel.transactionCount : 0,
    }))
  }

  const channelData = processChannelData()
  const hasChannelData = channelData.length > 0

  // Procesamiento de todos los datos usando las funciones definidas
  const datosTop10Items = procesarTop10Items()
  const datosTop10Clientes = procesarTop10ClientesPorFecha()
  const { medios: datosMediosPago, tipos: datosTiposPago } = procesarMediosDePago()
  const datosTopVendedores = procesarTopVendedores()
  const datosAnalisisTemporal = procesarAnalisisTemporal()

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xl" fullWidth>
      {/* Header del modal con gradiente y branding */}
      <DialogTitle
        sx={{
          m: 0,
          p: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "white",
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <AnalyticsIcon sx={{ fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Dashboard de Análisis de Ventas
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Análisis detallado de rendimiento y métricas de ventas
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: "white",
            "&:hover": {
              bgcolor: "rgba(255, 255, 255, 0.1)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, position: "relative", minHeight: 600 }}>
        {loading ? (
          // Estado de carga con spinner y mensajes informativos
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 600,
              gap: 3,
            }}
          >
            <CircularProgress size={80} color="primary" thickness={4} />
            <Typography variant="h5" color="text.secondary" sx={{ fontWeight: 600 }}>
              Procesando datos de análisis...
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", maxWidth: 400 }}>
              Analizando patrones de ventas, comportamiento de clientes y métricas de rendimiento
            </Typography>
          </Box>
        ) : (
          <>
            {/* Panel de resumen ejecutivo con KPIs principales */}
            <Box sx={{ p: 3, bgcolor: "grey.50" }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "text.primary" }}>
                Resumen Ejecutivo
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Ventas Totales"
                    value={formatCurrency(metricsGenerales.totalVentas || 0)}
                    subtitle={`${metricsGenerales.transaccionesUnicas} transacciones`}
                    icon={AttachMoneyIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Items Vendidos"
                    value={metricsGenerales.totalItems?.toLocaleString("es-AR") || "0"}
                    subtitle={`${metricsGenerales.itemsUnicos} productos únicos`}
                    icon={InventoryIcon}
                    color="primary"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Ticket Promedio"
                    value={formatCurrency(metricsGenerales.ticketPromedio || 0)}
                    subtitle={`${metricsGenerales.itemsPorTransaccion?.toFixed(1)} items/venta`}
                    icon={ShoppingCartIcon}
                    color="warning"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="Clientes Únicos"
                    value={metricsGenerales.clientesUnicos?.toLocaleString("es-AR") || "0"}
                    subtitle="Base de clientes activa"
                    icon={PersonIcon}
                    color="info"
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Sistema de pestañas para navegación entre análisis */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="chart tabs"
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  "& .MuiTab-root": {
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "0.95rem",
                  },
                }}
              >
                <Tab label="📊 Top Items" {...a11yProps(0)} />
                <Tab label="👥 Análisis de Clientes" {...a11yProps(1)} />
                <Tab label="💳 Medios de Pago" {...a11yProps(2)} />
                <Tab label="🏆 Performance Vendedores" {...a11yProps(3)} />
                <Tab label="📈 Tendencias Temporales" {...a11yProps(4)} />
                {hasChannelData && <Tab label="Análisis de Canales" {...a11yProps(5)} />}
              </Tabs>
            </Box>

            {/* PESTAÑA 1: ANÁLISIS DE TOP ITEMS */}
            <TabPanel value={tabValue} index={0}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Top 10 Items Más Vendidos
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico principal: ComposedChart con barras y línea */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Cantidad vs Monto de Ventas
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTop10Items}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={120} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "montoTotal") return [formatCurrency(value), "Monto Total"]
                            if (name === "cantidadVendida") return [value, "Cantidad Vendida"]
                            if (name === "precioPromedio") return [formatCurrency(value), "Precio Promedio"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="cantidadVendida" fill={COLORS[0]} name="Cantidad Vendida" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="precioPromedio"
                          stroke={COLORS[2]}
                          strokeWidth={3}
                          name="Precio Promedio"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Top 5 por monto */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top 5 por Monto
                    </Typography>
                    <List>
                      {datosTop10Items.slice(0, 5).map((item, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {item.nombre.length > 30 ? `${item.nombre.substring(0, 30)}...` : item.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(item.montoTotal)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {item.cantidadVendida} unidades
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 2: ANÁLISIS DE CLIENTES */}
            <TabPanel value={tabValue} index={1}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Análisis de Comportamiento de Clientes
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico principal: Ventas vs Frecuencia de compra */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top Clientes - Ventas vs Frecuencia
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTop10Clientes}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={120} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "totalVentas") return [formatCurrency(value), "Total Ventas"]
                            if (name === "cantidadCompras") return [value, "Cantidad Compras"]
                            if (name === "ticketPromedio") return [formatCurrency(value), "Ticket Promedio"]
                            if (name === "diasActivos") return [value, "Días Activos"]
                            return [value, name]
                          }}
                          labelFormatter={(label, payload) => {
                            const data = payload?.[0]?.payload
                            return data ? `${label} - Última compra: ${data.ultimaCompra}` : label
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalVentas" fill={COLORS[1]} name="Total Ventas" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="cantidadCompras"
                          stroke={COLORS[3]}
                          strokeWidth={3}
                          name="Cantidad Compras"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Clientes VIP */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Clientes VIP
                    </Typography>
                    <List>
                      {datosTop10Clientes.slice(0, 5).map((cliente, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index + 1], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {cliente.nombre.length > 25 ? `${cliente.nombre.substring(0, 25)}...` : cliente.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(cliente.totalVentas)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                  {cliente.cantidadCompras} compras
                                </Typography>
                                <br />
                                <Typography variant="caption" color="primary.main">
                                  Ticket: {formatCurrency(cliente.ticketPromedio)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 3: ANÁLISIS DE MEDIOS DE PAGO */}
            <TabPanel value={tabValue} index={2}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Análisis de Medios de Pago
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico de barras: Transacciones por medio */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Transacciones por Medio de Pago
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={datosMediosPago}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={80} interval={0} fontSize={11} />
                        <YAxis />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "cantidadTransacciones") return [value, "Transacciones"]
                            return [value, name]
                          }}
                          labelFormatter={(label, payload) => {
                            const data = payload?.[0]?.payload
                            return data ? `${label} (${data.tipo})` : label
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Bar dataKey="cantidadTransacciones" fill={COLORS[0]} name="Transacciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico circular: Distribución por monto */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Distribución por Monto
                    </Typography>
                    <ResponsiveContainer width="100%" height={350}>
                      <PieChart>
                        <Pie
                          data={datosMediosPago}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ nombre, montoTotal, percent }) => `${nombre}: ${(percent * 100).toFixed(1)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="montoTotal"
                        >
                          {datosMediosPago.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => [formatCurrency(value), "Monto Total"]}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Gráfico horizontal: Análisis por tipo de medio */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Análisis por Tipo de Medio de Pago
                    </Typography>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={datosTiposPago} layout="horizontal">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" />
                        <YAxis dataKey="nombre" type="category" width={100} />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "montoTotal") return [formatCurrency(value), "Monto Total"]
                            if (name === "cantidadTransacciones") return [value, "Transacciones"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar dataKey="montoTotal" fill={COLORS[1]} name="Monto Total" />
                        <Bar dataKey="cantidadTransacciones" fill={COLORS[4]} name="Transacciones" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 4: PERFORMANCE DE VENDEDORES */}
            <TabPanel value={tabValue} index={3}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Performance de Vendedores
              </Typography>

              <Grid container spacing={3}>
                {/* Gráfico combinado: Métricas múltiples por vendedor */}
                <Grid item xs={12} lg={8}>
                  <Paper elevation={2} sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Métricas de Performance por Vendedor
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <ComposedChart data={datosTopVendedores}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="nombre" angle={-45} textAnchor="end" height={100} interval={0} fontSize={11} />
                        <YAxis yAxisId="left" />
                        <YAxis yAxisId="right" orientation="right" />
                        <Tooltip
                          formatter={(value, name) => {
                            if (name === "totalVentas") return [formatCurrency(value), "Total Ventas"]
                            if (name === "cantidadItems") return [value, "Items Vendidos"]
                            if (name === "cantidadTransacciones") return [value, "Transacciones"]
                            if (name === "clientesUnicos") return [value, "Clientes Únicos"]
                            if (name === "ticketPromedio") return [formatCurrency(value), "Ticket Promedio"]
                            return [value, name]
                          }}
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar yAxisId="left" dataKey="totalVentas" fill={COLORS[0]} name="Total Ventas" />
                        <Bar yAxisId="left" dataKey="cantidadTransacciones" fill={COLORS[2]} name="Transacciones" />
                        <Line
                          yAxisId="right"
                          type="monotone"
                          dataKey="clientesUnicos"
                          stroke={COLORS[3]}
                          strokeWidth={3}
                          name="Clientes Únicos"
                        />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Paper>
                </Grid>

                {/* Panel lateral: Top performers */}
                <Grid item xs={12} lg={4}>
                  <Paper elevation={2} sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      Top Performers
                    </Typography>
                    <List>
                      {datosTopVendedores.slice(0, 5).map((vendedor, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                          <ListItemIcon>
                            <Avatar sx={{ bgcolor: COLORS[index + 2], width: 32, height: 32, fontSize: "0.875rem" }}>
                              {index + 1}
                            </Avatar>
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {vendedor.nombre}
                              </Typography>
                            }
                            secondary={
                              <Box>
                                <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                  {formatCurrency(vendedor.totalVentas)}
                                </Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">
                                  {vendedor.cantidadTransacciones} ventas • {vendedor.clientesUnicos} clientes
                                </Typography>
                                <br />
                                <Typography variant="caption" color="primary.main">
                                  Ticket: {formatCurrency(vendedor.ticketPromedio)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* PESTAÑA 5: ANÁLISIS TEMPORAL */}
            <TabPanel value={tabValue} index={4}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                Análisis de Tendencias Temporales
              </Typography>

              {/* Gráfico de área: Evolución temporal */}
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  Evolución de Ventas por Día
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart data={datosAnalisisTemporal}>
                    <defs>
                      <linearGradient id="colorVentas" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[1]} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS[1]} stopOpacity={0.1} />
                      </linearGradient>
                      <linearGradient id="colorTransacciones" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS[0]} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={COLORS[0]} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="fecha" angle={-45} textAnchor="end" height={80} fontSize={11} />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip
                      formatter={(value, name) => {
                        if (name === "totalVentas") return [formatCurrency(value), "Total Ventas"]
                        if (name === "cantidadTransacciones") return [value, "Transacciones"]
                        if (name === "ticketPromedio") return [formatCurrency(value), "Ticket Promedio"]
                        return [value, name]
                      }}
                      contentStyle={{
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      }}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="totalVentas"
                      stroke={COLORS[1]}
                      fillOpacity={1}
                      fill="url(#colorVentas)"
                      name="Total Ventas"
                      strokeWidth={2}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="cantidadTransacciones"
                      stroke={COLORS[0]}
                      strokeWidth={3}
                      name="Transacciones"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>

              {/* Métricas de resumen temporal */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Día con Mayor Venta"
                    value={
                      datosAnalisisTemporal.length > 0
                        ? datosAnalisisTemporal.reduce((max, dia) => (dia.totalVentas > max.totalVentas ? dia : max))
                            .fecha
                        : "N/A"
                    }
                    subtitle={
                      datosAnalisisTemporal.length > 0
                        ? formatCurrency(
                            datosAnalisisTemporal.reduce((max, dia) => (dia.totalVentas > max.totalVentas ? dia : max))
                              .totalVentas,
                          )
                        : "N/A"
                    }
                    icon={TrendingUpIcon}
                    color="success"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Promedio Diario"
                    value={
                      datosAnalisisTemporal.length > 0
                        ? formatCurrency(
                            datosAnalisisTemporal.reduce((sum, dia) => sum + dia.totalVentas, 0) /
                              datosAnalisisTemporal.length,
                          )
                        : formatCurrency(0)
                    }
                    subtitle={
                      datosAnalisisTemporal.length > 0
                        ? `${(datosAnalisisTemporal.reduce((sum, dia) => sum + dia.cantidadTransacciones, 0) / datosAnalisisTemporal.length).toFixed(1)} transacciones/día`
                        : "N/A"
                    }
                    icon={AnalyticsIcon}
                    color="info"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <MetricCard
                    title="Días Activos"
                    value={datosAnalisisTemporal.length}
                    subtitle="Período analizado"
                    icon={SellIcon}
                    color="warning"
                  />
                </Grid>
              </Grid>
            </TabPanel>
            {hasChannelData && (
              <TabPanel value={tabValue} index={5}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 3 }}>
                  Análisis de Canales de Venta
                </Typography>

                <Grid container spacing={3}>
                  {/* Performance Chart */}
                  <Grid item xs={12} lg={8}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Performance por Canal
                      </Typography>
                      <ResponsiveContainer width="100%" height={400}>
                        <ComposedChart data={channelData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} interval={0} fontSize={11} />
                          <YAxis yAxisId="left" />
                          <YAxis yAxisId="right" orientation="right" />
                          <Tooltip
                            formatter={(value, name) => {
                              if (name === "totalSales") return [`$${value.toLocaleString()}`, "Ventas Totales"]
                              if (name === "transactionCount") return [value, "Transacciones"]
                              return [value, name]
                            }}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Legend />
                          <Bar yAxisId="left" dataKey="totalSales" fill={COLORS[0]} name="Ventas Totales" />
                          <Line
                            yAxisId="right"
                            type="monotone"
                            dataKey="transactionCount"
                            stroke={COLORS[2]}
                            strokeWidth={3}
                            name="Transacciones"
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  {/* Channel Metrics */}
                  <Grid item xs={12} lg={4}>
                    <Paper sx={{ p: 2, height: "100%" }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Top 5 Canales
                      </Typography>
                      <List>
                        {channelData
                          .sort((a, b) => b.totalSales - a.totalSales)
                          .slice(0, 5)
                          .map((channel, index) => (
                            <ListItem key={channel.id} sx={{ px: 0 }}>
                              <ListItemIcon>
                                <Avatar
                                  sx={{ bgcolor: COLORS[index + 2], width: 32, height: 32, fontSize: "0.875rem" }}
                                >
                                  {index + 1}
                                </Avatar>
                              </ListItemIcon>
                              <ListItemText
                                primary={
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {channel.name.length > 25 ? `${channel.name.substring(0, 25)}...` : channel.name}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                                      ${channel.totalSales.toLocaleString()}
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="text.secondary">
                                      {channel.transactionCount} ventas • {channel.uniqueCustomers} clientes
                                    </Typography>
                                    <br />
                                    <Typography variant="caption" color="primary.main">
                                      Ticket: ${channel.averageTicket.toLocaleString()}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </ListItem>
                          ))}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Sales Distribution */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Distribución de Ventas
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={channelData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="totalSales"
                          >
                            {channelData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) => [`$${value.toLocaleString()}`, "Ventas"]}
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              border: "1px solid #e0e0e0",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>

                  {/* Channel Efficiency */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                        Eficiencia por Canal
                      </Typography>
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart
                          data={channelData.sort((a, b) => b.averageTicket - a.averageTicket)}
                          layout="horizontal"
                          margin={{ left: 100 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis type="number" />
                          <YAxis dataKey="name" type="category" width={100} />
                          <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, "Ticket Promedio"]} />
                          <Bar dataKey="averageTicket" fill={COLORS[1]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </Paper>
                  </Grid>
                </Grid>
              </TabPanel>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default GraficasDetallesVentasModal
